import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GEN_SHOPIFY_INTEGRATION_CODE,
  GET_ONBOARDING_PROGRESS,
  UPDATE_SKIP_SHOPIFY,
} from '~/services/onboarding';
import { StepComponentProps } from '../types';
import { ArrowPathIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { useLayoutContext } from '~/components/layout/Layout';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';

export const ConnectShopify = ({
  business,
  moveNextStep,
  submitForm,
  setSubmitForm,
  setDisable,
}: StepComponentProps) => {
  const { t } = useTranslation('onboarding');
  const { renderToast } = useLayoutContext();
  const [code, setCode] = useState('');
  const [shopName, setShopName] = useState(business.shopify?.shop_name || '');
  const [isShopifyNotConnected, setIsShopifyNotConnected] = useState(
    !business.shopify?.shop_name,
  );
  const [refreshBusinessStatus, { loading: loadingRefresStatus }] =
    useLazyQuery(GET_ONBOARDING_PROGRESS, {
      onCompleted: (data) => {
        setIsShopifyNotConnected(
          !data.response.data.business?.shopify?.shop_name,
        );
        setShopName(data.response.data.business?.shopify?.shop_name);
        setDisable(false);
      },
    });
  const [skipShopify, { loading: loadingSkipShopify }] =
    useMutation(UPDATE_SKIP_SHOPIFY);
  const [genCode, { loading }] = useMutation(GEN_SHOPIFY_INTEGRATION_CODE, {
    onCompleted: (data) => {
      if (data.response.data) {
        setCode(data.response.data.shopify_code);
      }
    },
    onError: (error) => {
      console.error(error);
      setSubmitForm(false);
    },
  });

  useEffect(() => {
    if (isShopifyNotConnected) {
      setDisable(true);
      genCode({
        variables: {
          input: {
            business_id: business?._id || '',
          },
        },
      });
    }
  }, []);

  const copyCodeToClipboard = () => {
    navigator.clipboard.writeText(code);
    renderToast({
      type: 'success',
      message: t('connect_shopify.code_copied'),
    });
  };

  const skipShopifyConnection = async () => {
    const { data } = await skipShopify({
      variables: {
        input: {
          business_id: business?._id || '',
        },
      },
    });
    if (!data.response.hasError) {
      moveNextStep(data.response.data);
    }
  };

  useEffect(() => {
    if (submitForm) {
      moveNextStep();
    }
  }, [submitForm]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-bold">{t('connect_shopify.title')}</h1>
      <p className="text-sm text-base-content">
        {t('connect_shopify.description')}
      </p>
      {isShopifyNotConnected && (
        <a
          className="text-blue-500 underline"
          href="https://apps.shopify.com/t-bit-sell-more-on-whatsapp"
          target="_blank"
          rel="noreferrer"
        >
          {t('connect_shopify.app_link')}
        </a>
      )}
      {isShopifyNotConnected && (
        <button
          className="cursor-pointer self-end"
          disabled={loadingRefresStatus || loading || loadingSkipShopify}
          onClick={() => {
            refreshBusinessStatus({
              fetchPolicy: 'no-cache',
            });
          }}
        >
          <ArrowPathIcon className="h-6 w-6" />
        </button>
      )}
      <div className="flex h-32 flex-col items-center justify-center rounded-xl border border-custom-surface-400 bg-custom-surface-200 px-4">
        {isShopifyNotConnected ? (
          <>
            {code && (!loading || !loadingSkipShopify) ? (
              <button
                className="text-bold flex items-center justify-center gap-5 p-4 text-2xl tracking-widest"
                onClick={copyCodeToClipboard}
              >
                {code}
                <ClipboardIcon className="h-6 w-6" />
              </button>
            ) : (
              <span className="loading loading-dots loading-md text-primary"></span>
            )}
          </>
        ) : (
          <>
            <p className="flex gap-4 text-lg">
              {t('connect_shopify.connected_successfully', {
                shopName: shopName,
              })}
              <CheckBadgeIcon className="shado-sm h-7 w-7 fill-success" />
            </p>
          </>
        )}
      </div>
      {isShopifyNotConnected && (
        <button
          disabled={loading || loadingSkipShopify || loadingRefresStatus}
          className="w-fit cursor-pointer self-end text-sm text-stone-700"
          onClick={skipShopifyConnection}
        >
          {t('connect_shopify.skip')}
        </button>
      )}
    </div>
  );
};
